export const pageViews = [
    'Intro Carousel',
    'Acknowledge Confirm',
    'Attachment Versions',
    'Edit Image',
    'Print Post',
    'Create Post',
    'Copy Post',
    'Edit Post',
    'Edit Draft',
    'Post Preview',
    'Relation Posts',
    'Post Detail',
    'Post Detail | Attachments',
    'Post Detail | Comments',
    'Post Detail | Visibility',
    'Post Detail | Invited',
    'Post Detail | Workflow',
    'Post Detail | Tasks',
    'Post Detail | Sub-Activities',
    'Post Detail | AI',
    'Post Detail | Labs',
    'Useful Links',
    'Licence Required',
    'Lightbox',
    'Survey Preview',
    'All Survey Answers',
    'Answer Survey',
    'My Survey Answers',
    'All Mission Answers',
    'Mission Preview',
    'Answer Mission',
    'My Mission Answers',
    'Mission Statistics',
    'Mission Question Comments',
    'Statistics | Workflow',
    'Statistics | Base',
    'Multiple Transition',
    'Multiple Transition Report',
    'Transition Edit',
    'Transition',
    'My Space',
    'My Space | Workflows',
    'My Space | Drafts and Scheduled',
    'My Space | Mentions',
    'My Space | Favourites',
    'My Space | Assigned Tasks',
    'My Space | Monitor Tasks',
    'Manage Collections',
    'Search | All',
    'Search | Posts',
    'Search | Comments',
    'Search | Media',
    'Search | Documents',
    'Unsubscribe',
    'Home Config List',
    'Home Config',
    'Home Preview',
    'Customer Feedback',
    'Notification Settings',
    'Open Source Licenses',
    'Profile',
    'Profile Edit',
    'People',
    'Community Detail',
    'Community Posts',
    'My Communities Feed',
    'Community Posts Archive',
    'Home DW',
    'Login',
    'Password Forgot',
    'AI Notes List',
    'AI Note Detail',
    'AI Note Print',
    'AI Note Post Preview',
    'Admin | Dashboard',
    'Gantt',
] as const;

export type PageView = (typeof pageViews)[number];

export interface AdminCustomEventPayloads {
    isp_admin_useful_link_to_new_clicked: undefined;
    isp_admin_dashboard_widget_users: {
        type: 'list' | 'detail' | 'create' | 'import';
    };
    isp_admin_dashboard_widget_groups: {
        type: 'list' | 'detail' | 'create' | 'import';
    };
    isp_admin_dashboard_widget_communities: {
        type: 'list' | 'draft' | 'detail' | 'create';
    };
    isp_admin_dashboard_widget_xlearning: undefined;
    isp_admin_dashboard_widget_latest_releases: undefined;
    isp_admin_dashboard_widget_support: undefined;
    isp_admin_dashboard_widget_licences: undefined;
}

export interface CustomEventPayloads extends AdminCustomEventPayloads {
    isp_embedded_image_added: undefined;
    isp_attachment_reference_added: undefined;

    isp_favourite_post_saved: { collections: number };
    isp_favourite_post_updated: { collections: number };
    isp_favourite_post_removed: undefined;

    isp_customer_feedback_form_submit: undefined;
    isp_customer_feedback_form_opened: undefined;
    isp_customer_feedback_review_open: undefined;

    isp_ilab_created: {
        source:
            'voice_rec' | 'audio' | 'video' | 'document' | 'free_text' | 'post';
    };
    isp_ilab_summary_generated: {
        mode: 'generate' | 'regenerate';
        tone: 'formal' | 'casual';
        length: 'short' | 'medium' | 'long';
        uses_posts: boolean;
        uses_post_data: boolean;
        uses_post_comments: boolean;
        uses_post_workflow: boolean;
        uses_post_attachments: boolean;
    };
    isp_ilab_report_generated: {
        mode: 'generate' | 'regenerate';
        style: 'descriptive' | 'decisional' | 'operative';
        detail: 'low' | 'intermediate' | 'high';
        uses_posts: boolean;
        uses_post_data: boolean;
        uses_post_comments: boolean;
        uses_post_workflow: boolean;
        uses_post_attachments: boolean;
    };
    isp_ilab_scripted_generated: {
        source: 'plain' | 'template';
        uses_posts: boolean;
        uses_post_data: boolean;
        uses_post_comments: boolean;
        uses_post_workflow: boolean;
        uses_post_attachments: boolean;
    };

    isp_ilab_template_saved: undefined;
    isp_ilab_template_selected: undefined;

    isp_activities_all_showed: undefined;
    isp_activities_active_showed: undefined;
    isp_gantt_view_changed: {
        type: 'weeks' | 'months' | 'trimesters' | 'years';
    };
    isp_gantt_date_filter_changed: undefined;

    isp_activity_post_copied: {
        copy_subactivities: boolean;
        copy_attachments: boolean;
        keep_assignees: boolean;
        start_data: 'keep' | 'remove' | 'new';
    };

    isp_web_relations_show_community_clicked: undefined;
    isp_web_relations_create_post_clicked: undefined;
    isp_web_relations_link_post_clicked: undefined;
    isp_web_relations_dialog_create_post_clicked: undefined;
    isp_web_relations_dialog_link_post_clicked: undefined;
    isp_web_relations_post_created: undefined;
    isp_web_relations_post_linked: undefined;

    isp_field_inline_edited: {
        post_type: GAPostType;
        field_type: GAFieldType;
        source: 'relation' | 'activity' | 'post_list';
    };

    isp_post_creation_completed: {
        type: 'draft' | 'scheduled' | 'publish';
        post_type: GAPostType;
        source: 'null' | 'draft' | 'copy' | 'lab';
    };

    isp_ai_conversational_opened: {
        source: 'home' | 'community' | 'post' | 'attachment';
    };
    isp_ai_conversational_message_sent: {
        type: 'plain' | 'attachment_from_picker' | 'attachment_from_action';
    };
    isp_ai_conversational_answer_received: { has_sources: boolean };
    isp_ai_history_conversation_opened: undefined;
    isp_ai_sources_list_expanded: undefined;
    isp_ai_source_opened: {
        source: 'inline' | 'list';
        type: 'post' | 'comment' | 'attachment';
    };
    isp_ai_conversational_discovery_opened: undefined;
    isp_ai_conversational_discovery_skipped: { shown_count: number };
    isp_ai_conversational_menu_video_opened: undefined;

    isp_ai_guide_center_opened: undefined;
    isp_ai_guide_center_message_sent: undefined;

    isp_ai_text_action_requested: { action: GAAiTextAction };
    isp_ai_text_action_confirmed: {
        action: GAAiTextAction;
        tone?: GAAiToneOfVoice;
    };

    isp_trigger_toast_opened: undefined;
    isp_trigger_toast_dismissed: undefined;
}

export type GAAiTextAction = 'improve' | 'change_tone' | 'extend' | 'summarize';

export type GAAiToneOfVoice =
    | 'formal'
    | 'casual'
    | 'neutral'
    | 'persuasive'
    | 'excited'
    | 'empathetic'
    | 'authoritarian'
    | 'friendly'
    | 'confident'
    | 'positive';

export type CustomEventsWithPayload = keyof {
    [
        E in keyof CustomEventPayloads as CustomEventPayloads[E] extends undefined
            ? never
            : E
    ]: any;
};

export type CustomEventsWithoutPayload = keyof {
    [
        E in keyof CustomEventPayloads as CustomEventPayloads[E] extends undefined
            ? E
            : never
    ]: any;
};

export type GAPostType = 'custom' | 'activity' | 'event' | 'survey';

export type GAFieldType =
    | 'numeric'
    | 'date'
    | 'string'
    | 'text_area'
    | 'html_area'
    | 'flag'
    | 'enum'
    | 'rating'
    | 'link'
    | 'user_picker'
    | 'group_picker'
    | 'post_picker'
    | 'file_picker'
    | 'unknown'
    //base fields
    | 'title'
    | 'assignee'
    | 'expected_start'
    | 'expected_end'
    | 'priority'
    | 'progress_percentage';
